import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export default function BranchesPage() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', location: '' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await api.get('/branches');
      setBranches(data);
    } catch (err) { toast.error('Failed to load branches'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/branches/${editing.id}`, form);
        toast.success('Branch updated');
      } else {
        await api.post('/branches', form);
        toast.success('Branch created');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ name: '', location: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this branch?')) return;
    try {
      await api.delete(`/branches/${id}`);
      toast.success('Branch deleted');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const openEdit = (branch) => {
    setEditing(branch);
    setForm({ name: branch.name, location: branch.location || '' });
    setDialogOpen(true);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', location: '' });
    setDialogOpen(true);
  };

  return (
    <div data-testid="branches-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-barlow font-bold text-3xl text-foreground tracking-tight">Branches</h1>
          <p className="text-muted-foreground text-sm mt-1">{branches.length} branches</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="bg-brand hover:bg-brand-hover text-brand-foreground" data-testid="create-branch-btn">
              <Plus className="w-4 h-4 me-2" /> Add Branch
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-barlow text-xl">{editing ? 'Edit Branch' : 'Create Branch'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="branch-form">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="branch-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} data-testid="branch-location-input" />
              </div>
              <Button type="submit" className="w-full bg-brand hover:bg-brand-hover text-brand-foreground" data-testid="branch-submit-btn">
                {editing ? 'Update' : 'Create'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <p className="text-muted-foreground col-span-full text-center py-8">Loading...</p>
        ) : branches.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <Building2 className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
            <p className="text-muted-foreground">No branches yet</p>
          </div>
        ) : branches.map(b => (
          <Card key={b.id} className="bg-card border-border shadow-sm hover:border-brand/50 transition-colors" data-testid={`branch-card-${b.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-barlow font-semibold text-lg text-foreground">{b.name}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">{b.location || 'No location'}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(b)} data-testid={`branch-edit-${b.id}`}>
                    <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(b.id)} data-testid={`branch-delete-${b.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
